import 'chartjs-adapter-date-fns'

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import cx from 'classnames'
import { useCallback, useRef, useState } from 'react'
import { Bar, getElementsAtEvent } from 'react-chartjs-2'

import { type ChartValueClickEventProps, MergedBarLineChartProps } from '../Charts.types'
import {
  DAILY_BAR_AND_LINE_OPTIONS,
  DEFAULT_BAR_AND_LINE_OPTIONS,
  WEEKLY_BAR_AND_LINE_OPTIONS,
} from '../configs/options'

ChartJS.register(CategoryScale, LinearScale, TimeScale, BarElement, Title, Tooltip, Legend)

function determineChartType(option: string) {
  switch (option) {
    case 'daily':
      return DAILY_BAR_AND_LINE_OPTIONS
    case 'weekly':
      return WEEKLY_BAR_AND_LINE_OPTIONS()
    default:
      return DEFAULT_BAR_AND_LINE_OPTIONS
  }
}

export function MergedBarLineChart(props: MergedBarLineChartProps) {
  return <MergedBarChart {...props} />
}

export function MergedBarChart({
  bar,
  line,
  className = '',
  onClick,
  option = 'default',
  customTooltip = null,
  onValueClick,
}: MergedBarLineChartProps) {
  const labels = bar.labels
  const classNames = className
  const chartRef = useRef()
  const bD = bar.datasets.map((d) => ({ ...d, yAxisID: 'leftYAxis' }))
  const lD = line.datasets.map((d) => ({ ...d, yAxisID: 'rightYAxis' }))
  const _data = { labels, datasets: [...lD, ...bD] } as unknown as ChartData<'bar'>
  const selectedOption = {
    ...determineChartType(option),
    onClick: (event: ChartValueClickEventProps, ctx?: { index?: number }[]) => {
      if (ctx && ctx.length > 0 && ctx[0].index !== undefined) {
        const label = event.chart.config.data.labels[ctx[0].index]
        onValueClick && onValueClick(label)
      }
    },
  }
  const _options = Object.assign({}, selectedOption) as unknown as ChartOptions

  const [showCustom, setShowCustom] = useState(false)

  if (customTooltip) {
    const base = _options?.plugins ?? {}
    _options.plugins = { ...base, tooltip: { enabled: false } }
  }

  const toggleCustomTooltip = useCallback(
    () => customTooltip && setShowCustom(!showCustom),
    [customTooltip, showCustom]
  )

  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (chartRef?.current && onClick) {
        onClick(getElementsAtEvent(chartRef?.current, event))
      }
    },
    [onClick]
  )

  return (
    <>
      <Bar
        className={cx('h-[232px] max-h-[232px]', classNames, {
          'cursor-pointer': !!onClick,
          relative: customTooltip,
        })}
        ref={chartRef}
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        options={_options}
        data={_data}
        onClick={clickHandler}
        onMouseEnter={toggleCustomTooltip}
        onMouseLeave={toggleCustomTooltip}
      />
      {customTooltip && showCustom && customTooltip}
    </>
  )
}
